import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  reload,
  User as FirebaseUser,
} from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from './firebase';
import { getPrivacyCompliantValue } from '@/config/privacy';
import { getClientIP } from './complianceApi';
import type { User, Organization, Form, Submission, RegisterRequest } from '@/types';

// Safe timestamp conversion — handles Firestore Timestamps, Dates, strings, and missing values
const safeToDate = (value: any, fallback?: Date): Date | undefined => {
  if (!value) return fallback;
  if (value && typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? fallback || new Date() : d;
  }
  return fallback;
};

// Helper to wait for auth to be ready
let authReadyPromise: Promise<FirebaseUser | null> | null = null;

const waitForAuthReady = (): Promise<FirebaseUser | null> => {
  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        unsubscribe();
        resolve(user);
      });
    });
  }
  return authReadyPromise;
};

// Simple helper to get current user or throw error
const getCurrentAuthUser = async () => {
  let user = auth.currentUser;

  // If no current user, wait for auth state to be determined
  if (!user) {
    user = await waitForAuthReady();
  }

  if (!user) {
    throw new Error('Not authenticated');
  }

  return user;
};

// Helper to get user's organization ID
const getUserOrganizationId = async (userId: string): Promise<string> => {
  try {
    // Get user document using UID as document ID (matching our new pattern)
    const userDoc = await getDoc(doc(db, 'users', userId));

    if (!userDoc.exists()) {
      console.warn(
        '[getUserOrganizationId] User profile not found, using userId as organizationId'
      );
      return userId; // Fallback to using userId as organizationId
    }

    const userData = userDoc.data();
    const organizationId = userData.organizationId;

    if (!organizationId) {
      console.warn(
        '[getUserOrganizationId] No organizationId in user profile, using userId as organizationId'
      );
      return userId; // Fallback to using userId as organizationId
    }

    return organizationId;
  } catch (error) {
    console.error('[getUserOrganizationId] Error getting organization ID:', error);
    return userId; // Fallback to using userId as organizationId
  }
};

// Auth Functions
export const firebaseAuth = {
  async login(email: string, password: string) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Get user document from Firestore using UID as document ID
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

      if (!userDoc.exists()) {
        throw new Error('User profile not found');
      }

      const userData = userDoc.data();

      // Get organization data
      const orgDoc = await getDoc(doc(db, 'organizations', userData.organizationId));
      if (!orgDoc.exists()) {
        throw new Error('Organization not found');
      }

      const orgData = orgDoc.data();

      return {
        user: {
          id: firebaseUser.uid,
          email: firebaseUser.email!,
          organizationId: userData.organizationId,
          role: userData.role,
          createdAt: safeToDate(userData.createdAt, new Date())!,
        } as User,
        organization: {
          id: userData.organizationId,
          name: orgData.name,
          email: orgData.email,
          facilityType: orgData.facilityType,
          createdAt: safeToDate(orgData.createdAt, new Date())!,
        } as Organization,
        token: await firebaseUser.getIdToken(),
      };
    } catch (error: any) {
      throw new Error(error.message || 'Login failed');
    }
  },

  async register(data: RegisterRequest) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password);
      const firebaseUser = userCredential.user;

      // Update user profile
      await updateProfile(firebaseUser, {
        displayName: data.organizationName,
      });

      // Create organization document
      const orgRef = await addDoc(collection(db, 'organizations'), {
        name: data.organizationName,
        email: data.email,
        facilityType: data.facilityType,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Create user document with UID as document ID
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        organizationId: orgRef.id,
        role: 'owner',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return {
        user: {
          id: firebaseUser.uid,
          email: firebaseUser.email!,
          organizationId: orgRef.id,
          role: 'owner',
          createdAt: new Date(),
        } as User,
        organization: {
          id: orgRef.id,
          name: data.organizationName,
          email: data.email,
          facilityType: data.facilityType,
          createdAt: new Date(),
        } as Organization,
        token: await firebaseUser.getIdToken(),
      };
    } catch (error: any) {
      throw new Error(error.message || 'Registration failed');
    }
  },

  async logout() {
    await signOut(auth);
  },

  async getCurrentUser() {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('Not authenticated');
      }

      const q = query(collection(db, 'users'), where('uid', '==', firebaseUser.uid));
      const querySnapshot = await getDocs(q);

      let userData;
      if (querySnapshot.empty) {
        console.warn('[getCurrentUser] User profile not found, creating basic profile');
        // Create a basic user profile with fallback data
        userData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          organizationId: firebaseUser.uid, // Fallback to user ID as org ID
          displayName: firebaseUser.displayName || firebaseUser.email,
          createdAt: new Date(),
        };
      } else {
        const userDoc = querySnapshot.docs[0];
        userData = userDoc.data();

        // Ensure organizationId exists with fallback
        if (!userData.organizationId) {
          console.warn('[getCurrentUser] No organizationId in profile, using userId as fallback');
          userData.organizationId = firebaseUser.uid;
        }
      }

      // Try to get organization data with fallback
      let orgData;
      try {
        const orgDoc = await getDoc(doc(db, 'organizations', userData.organizationId));
        if (orgDoc.exists()) {
          orgData = orgDoc.data();
        } else {
          console.warn('[getCurrentUser] Organization not found, creating fallback org data');
          orgData = {
            id: userData.organizationId,
            name: userData.displayName || userData.email || 'Organization',
            type: 'facility',
            createdAt: new Date(),
          };
        }
      } catch (orgError) {
        console.error('[getCurrentUser] Error fetching organization:', orgError);
        orgData = {
          id: userData.organizationId,
          name: userData.displayName || userData.email || 'Organization',
          type: 'facility',
          createdAt: new Date(),
        };
      }

      return {
        user: {
          id: firebaseUser.uid,
          email: firebaseUser.email!,
          emailVerified: firebaseUser.emailVerified,
          organizationId: userData.organizationId,
          role: userData.role || 'admin',
          createdAt: safeToDate(userData.createdAt, new Date())!,
        } as User,
        organization: {
          id: userData.organizationId,
          name: orgData?.name || userData.displayName || userData.email || 'Organization',
          email: orgData?.email || userData.email || '',
          facilityType: orgData?.facilityType || 'riding_school',
          createdAt: safeToDate(orgData?.createdAt, new Date())!,
        } as Organization,
        token: await firebaseUser.getIdToken(),
      };
    } catch (error: any) {
      console.error('[getCurrentUser] Error:', error);
      console.error('[getCurrentUser] Error details:', error.message, error.stack);
      throw error; // This one needs to throw since auth provider depends on it
    }
  },

  async sendEmailVerification(email?: string) {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('Not authenticated');
      }

      await sendEmailVerification(firebaseUser);
      return { success: true, message: 'Verification email sent' };
    } catch (error: any) {
      console.error('sendEmailVerification error:', error);
      throw new Error(error.message || 'Failed to send verification email');
    }
  },

  async checkEmailVerification() {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('Not authenticated');
      }

      // Reload user to get latest verification status
      await reload(firebaseUser);

      return {
        emailVerified: firebaseUser.emailVerified,
        email: firebaseUser.email,
      };
    } catch (error: any) {
      console.error('checkEmailVerification error:', error);
      throw new Error(error.message || 'Failed to check verification status');
    }
  },

  async sendPasswordReset(email: string) {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true, message: 'Password reset email sent' };
    } catch (error: any) {
      console.error('sendPasswordReset error:', error);

      // Provide user-friendly error messages
      let errorMessage = 'Failed to send password reset email';

      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please try again later';
      }

      throw new Error(errorMessage);
    }
  },
};

// Utility function to generate unique share links
const generatePublicLink = (title: string): string => {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();

  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substr(2, 5);

  return `${slug}-${timestamp}${random}`;
};

// Forms Functions
export const firebaseForms = {
  async createForm(formData: Partial<Form>) {
    try {
      const user = await getCurrentAuthUser();
      const organizationId = await getUserOrganizationId(user.uid);

      // If a custom URL ending is set, route through the slug-claim Cloud Function
      // so the publicLink is set to the user's chosen slug atomically with collision check.
      const customSlug = (formData.settings as any)?.customUrlEnding?.trim();
      if (customSlug) {
        const callable = httpsCallable(functions, 'claimSlugAndCreateForm');
        const result = await callable({ formData });
        const data = result.data as any;
        return {
          ...data,
          createdAt: safeToDate(data.createdAt, new Date())!,
          updatedAt: safeToDate(data.updatedAt, new Date())!,
        } as Form;
      }

      // Generate unique public link
      const publicLink = generatePublicLink(formData.title || 'form');

      // Ensure default email settings if not provided
      const defaultEmailSettings = {
        enabled: true,
        sendToParticipant: true,
        sendToOrganization: false,
        organizationEmail: '',
        participantSubjectLine: '',
        organizationSubjectLine: '',
        includeFormResults: true,
        customParticipantMessage: '',
        customOrganizationMessage: '',
      };

      // Deep strip undefined values — Firestore rejects them at any nesting level
      const stripUndefined = (obj: Record<string, any>): Record<string, any> => {
        const result: Record<string, any> = {};
        for (const [key, value] of Object.entries(obj)) {
          if (value === undefined) continue;
          if (Array.isArray(value)) {
            result[key] = value.map((item) =>
              item !== null && typeof item === 'object' && !(item instanceof Date)
                ? stripUndefined(item)
                : item
            );
          } else if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
            result[key] = stripUndefined(value);
          } else {
            result[key] = value;
          }
        }
        return result;
      };

      const cleanFormData = stripUndefined(formData as Record<string, any>);

      const docRef = await addDoc(collection(db, 'forms'), {
        ...cleanFormData,
        emailSettings: {
          ...defaultEmailSettings,
          ...(formData.emailSettings || {}),
        },
        publicLink,
        organizationId,
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: formData.isActive ?? false, // Respect passed value, default to false
      });

      return {
        id: docRef.id,
        ...formData,
        publicLink,
        organizationId,
        createdBy: user.uid,
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: formData.isActive ?? false,
      } as Form;
    } catch (error: any) {
      console.error('createForm error:', error);
      throw error;
    }
  },

  async updateForm(formId: string, formData: Partial<Form>) {
    try {
      const user = await getCurrentAuthUser();

      // If a custom URL ending is being set/changed, route through the slug-claim
      // Cloud Function so publicLink updates atomically with collision check.
      const customSlug = (formData.settings as any)?.customUrlEnding?.trim();
      if (customSlug) {
        const currentDoc = await getDoc(doc(db, 'forms', formId));
        const currentPublicLink = currentDoc.exists() ? currentDoc.data().publicLink : null;
        if (customSlug !== currentPublicLink) {
          const callable = httpsCallable(functions, 'claimSlugAndUpdateForm');
          const result = await callable({ formId, formData });
          const data = result.data as any;
          return {
            ...data,
            createdAt: safeToDate(data.createdAt, new Date())!,
            updatedAt: safeToDate(data.updatedAt, new Date())!,
          } as Form;
        }
      }

      // Deep strip undefined values — Firestore rejects them at any nesting level.
      // Handles undefined inside arrays (which the previous implementation missed —
      // an undefined element became a primitive `undefined` in the result, which
      // Firestore then rejected). Now any undefined element is dropped from the array.
      const stripUndefinedDeep = (val: any): any => {
        if (val === undefined) return undefined;
        if (val === null) return null;
        if (Array.isArray(val)) {
          return val
            .map((item) => stripUndefinedDeep(item))
            .filter((item) => item !== undefined);
        }
        if (val instanceof Date) return val;
        if (typeof val === 'object') {
          const result: Record<string, any> = {};
          for (const [k, v] of Object.entries(val)) {
            const cleaned = stripUndefinedDeep(v);
            if (cleaned !== undefined) result[k] = cleaned;
          }
          return result;
        }
        return val;
      };

      const cleanFormData = stripUndefinedDeep(formData as Record<string, any>) as Record<
        string,
        any
      >;

      await updateDoc(doc(db, 'forms', formId), {
        ...cleanFormData,
        updatedAt: serverTimestamp(),
      });

      // Fetch the complete updated form to return all fields including publicLink
      const updatedFormDoc = await getDoc(doc(db, 'forms', formId));
      if (!updatedFormDoc.exists()) {
        throw new Error('Form not found after update');
      }

      const data = updatedFormDoc.data();
      return {
        id: updatedFormDoc.id,
        ...data,
        createdAt: safeToDate(data.createdAt, new Date())!,
        updatedAt: safeToDate(data.updatedAt, new Date())!,
      } as Form;
    } catch (error: any) {
      console.error('updateForm error:', error);
      throw error;
    }
  },

  async getForm(formId: string) {
    const formDoc = await getDoc(doc(db, 'forms', formId));
    if (!formDoc.exists()) {
      throw new Error('Form not found');
    }

    const data = formDoc.data();
    return {
      id: formDoc.id,
      ...data,
      createdAt: safeToDate(data.createdAt, new Date())!,
      updatedAt: safeToDate(data.updatedAt, new Date())!,
    } as Form;
  },

  async getForms() {
    try {
      console.log('[getForms] Starting...');

      // Check if we have a current user first
      let user;
      try {
        user = await getCurrentAuthUser();
        console.log('[getForms] User authenticated:', user.uid);
      } catch (authError) {
        console.error('[getForms] Authentication error:', authError);
        return []; // Return empty array instead of throwing
      }

      let organizationId;
      try {
        organizationId = await getUserOrganizationId(user.uid);
        console.log('[getForms] Organization ID:', organizationId);
      } catch (orgError) {
        console.error('[getForms] Organization ID error:', orgError);
        organizationId = user.uid; // Fallback to user ID
      }

      // Try to find forms with organization ID first
      let q = query(
        collection(db, 'forms'),
        where('organizationId', '==', organizationId),
        orderBy('updatedAt', 'desc')
      );

      let querySnapshot = await getDocs(q);
      console.log(
        '[getForms] Found',
        querySnapshot.docs.length,
        'forms with organizationId:',
        organizationId
      );

      // If no forms found and organizationId is different from user.uid, also try user.uid
      if (querySnapshot.docs.length === 0 && organizationId !== user.uid) {
        console.log(
          '[getForms] No forms found with organizationId, trying with user.uid:',
          user.uid
        );
        q = query(
          collection(db, 'forms'),
          where('organizationId', '==', user.uid),
          orderBy('updatedAt', 'desc')
        );
        querySnapshot = await getDocs(q);
        console.log('[getForms] Found', querySnapshot.docs.length, 'forms with user.uid');
      }

      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: safeToDate(data.createdAt, new Date())!,
          updatedAt: safeToDate(data.updatedAt, new Date())!,
        };
      }) as Form[];
    } catch (error: any) {
      console.error('[getForms] Error:', error);
      console.error('[getForms] Error details:', error.message, error.stack);
      // Return empty array instead of throwing to prevent breaking the UI
      return [];
    }
  },

  async deleteForm(formId: string) {
    await deleteDoc(doc(db, 'forms', formId));
  },

  async getPublicForm(publicLink: string) {
    const q = query(
      collection(db, 'forms'),
      where('publicLink', '==', publicLink),
      where('isActive', '==', true)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error('Form not found or not active');
    }

    const formDoc = querySnapshot.docs[0];
    const data = formDoc.data();

    return {
      id: formDoc.id,
      ...data,
      createdAt: safeToDate(data.createdAt, new Date())!,
      updatedAt: safeToDate(data.updatedAt, new Date())!,
    } as Form;
  },
};

// Submissions Functions
export const firebaseSubmissions = {
  // Helper function to extract participant email from form data
  extractParticipantEmail(formData: Record<string, any>): string | null {
    // Look for email field in form data
    for (const [, value] of Object.entries(formData)) {
      if (typeof value === 'string' && value.includes('@') && value.includes('.')) {
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(value)) {
          return value;
        }
      }
    }
    return null;
  },

  // Helper function to extract participant name from form data
  extractParticipantName(formData: Record<string, any>): string {
    // First: prioritize fields with "name" in the key
    for (const [key, value] of Object.entries(formData)) {
      const keyLower = key.toLowerCase();
      if (keyLower.includes('name') && !keyLower.includes('email')) {
        // Handle compound name fields (e.g., { firstName, lastName })
        if (typeof value === 'object' && value !== null) {
          const parts = [value.firstName, value.lastName].filter(Boolean);
          if (parts.length > 0) return parts.join(' ');
        }
        if (typeof value === 'string' && value.trim()) {
          return value.trim();
        }
      }
    }
    // Fallback: first string that looks like a name
    for (const [, value] of Object.entries(formData)) {
      if (typeof value === 'string' && value.trim() && (value.includes(' ') || value.length > 2)) {
        return value;
      }
    }
    return 'Participant';
  },

  async createSubmission(formId: string, submissionData: any) {
    try {
      // Use organizationId from submission data if available (public submissions),
      // otherwise fetch via authenticated getForm
      let organizationId = submissionData.organizationId;
      if (!organizationId) {
        const form = await firebaseForms.getForm(formId);
        organizationId = form.organizationId;
      }

      const ipAddress = await getClientIP().catch(() => 'unknown');

      // Deep strip undefined values — Firestore rejects them at any nesting level.
      // Drops undefined elements from arrays too (the previous implementation left
      // them as primitive undefined which Firestore then rejected).
      const stripUndefinedDeep = (val: any): any => {
        if (val === undefined) return undefined;
        if (val === null) return null;
        if (Array.isArray(val)) {
          return val
            .map((item) => stripUndefinedDeep(item))
            .filter((item) => item !== undefined);
        }
        if (val instanceof Date) return val;
        if (typeof val === 'object') {
          const result: Record<string, any> = {};
          for (const [k, v] of Object.entries(val)) {
            const cleaned = stripUndefinedDeep(v);
            if (cleaned !== undefined) result[k] = cleaned;
          }
          return result;
        }
        return val;
      };

      const cleanSubmissionData = stripUndefinedDeep(submissionData);

      // Prepare submission document
      const submissionDoc: any = {
        formId,
        formData: cleanSubmissionData,
        submittedAt: serverTimestamp(),
        ipAddress: getPrivacyCompliantValue(ipAddress, 'ip'),
        userAgent: getPrivacyCompliantValue(navigator.userAgent, 'userAgent'),
        status: 'pending',
        organizationId: organizationId,
      };

      // Only include auditTrailId if it's not undefined
      if (submissionData.auditTrailId !== undefined && submissionData.auditTrailId !== null) {
        submissionDoc.auditTrailId = submissionData.auditTrailId;
      }

      // Top-level fields used by the payment-orphan reconciliation pipeline.
      // The Stripe webhook + scheduled audit + recovery flow all query
      // `submissions where paymentIntentId == X` — that field MUST be at the
      // top level of the doc, not nested inside formData. Same goes for
      // participantEmail/participantName so dashboard columns populate.
      if (submissionData.paymentIntentId) {
        submissionDoc.paymentIntentId = submissionData.paymentIntentId;
      }
      if (submissionData.participantEmail) {
        submissionDoc.participantEmail = submissionData.participantEmail;
      }
      if (submissionData.participantName) {
        submissionDoc.participantName = submissionData.participantName;
      }

      const docRef = await addDoc(collection(db, 'submissions'), submissionDoc);

      // Email notifications handled by backend Firestore trigger (onSubmissionCreated)

      return {
        id: docRef.id,
        formId,
        organizationId: organizationId,
        formData: submissionData,
        ipAddress: '',
        submittedAt: new Date(),
        status: 'pending' as const,
        auditTrailId: submissionData.auditTrailId,
      } as Submission;
    } catch (error) {
      console.error('Error creating submission:', error);
      throw error;
    }
  },

  async getSubmissions(formId?: string) {
    try {
      console.log('[getSubmissions] Starting...');

      // Check if we have a current user first
      let user;
      try {
        user = await getCurrentAuthUser();
        console.log('[getSubmissions] User authenticated:', user.uid);
      } catch (authError) {
        console.error('[getSubmissions] Authentication error:', authError);
        return []; // Return empty array instead of throwing
      }

      let organizationId;
      try {
        organizationId = await getUserOrganizationId(user.uid);
        console.log('[getSubmissions] Organization ID:', organizationId);
      } catch (orgError) {
        console.error('[getSubmissions] Organization ID error:', orgError);
        organizationId = user.uid; // Fallback to user ID
      }

      let submissions = [];

      if (formId) {
        console.log('[getSubmissions] Fetching submissions for specific form:', formId);
        // Query by specific form AND organization to match security rules
        const q = query(
          collection(db, 'submissions'),
          where('formId', '==', formId),
          where('organizationId', '==', organizationId),
          orderBy('submittedAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        submissions = querySnapshot.docs;
        console.log('[getSubmissions] Found', submissions.length, 'submissions for form');
      } else {
        // Query submissions by organization ID with dual-query pattern for backward compatibility
        // First try to get submissions with organizationId field
        try {
          console.log('[getSubmissions] Trying to query by organizationId:', organizationId);
          const q = query(
            collection(db, 'submissions'),
            where('organizationId', '==', organizationId),
            orderBy('submittedAt', 'desc')
          );
          const querySnapshot = await getDocs(q);
          submissions = querySnapshot.docs;
          console.log('[getSubmissions] Found', submissions.length, 'submissions by org');

          // If no submissions found and organizationId != userId, try with userId as organizationId (backward compatibility)
          if (submissions.length === 0 && organizationId !== user.uid) {
            console.log(
              '[getSubmissions] No submissions found, trying with user UID as organizationId:',
              user.uid
            );
            const fallbackQuery = query(
              collection(db, 'submissions'),
              where('organizationId', '==', user.uid),
              orderBy('submittedAt', 'desc')
            );
            const fallbackSnapshot = await getDocs(fallbackQuery);
            submissions = fallbackSnapshot.docs;
            console.log('[getSubmissions] Found', submissions.length, 'submissions by user UID');
          }
        } catch (indexError: any) {
          // SECURITY: DO NOT fall back to downloading all submissions - this violates data isolation
          console.error(
            '[getSubmissions] Index error - aborting for security:',
            indexError.message
          );
          console.error(
            '[getSubmissions] SECURITY: Refusing to download all submissions for client-side filtering'
          );

          // Log security event
          console.warn(
            '[SECURITY] Attempted to query submissions without proper index - potential data exposure prevented'
          );

          // Return empty array instead of exposing all data
          submissions = [];
          console.log('[getSubmissions] Returning empty array due to security restrictions');
        }
      }

      return submissions.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          formId: data.formId,
          organizationId: data.organizationId || organizationId,
          formData: data.formData || data.data || {},
          status: data.status || 'pending',
          submittedAt: safeToDate(data.submittedAt, new Date())!,
          reviewedAt: safeToDate(data.reviewedAt),
          reviewedBy: data.reviewedBy,
          ipAddress: data.ipAddress || '',
          notes: data.notes,
          auditTrailId: data.auditTrailId,
        } as Submission;
      });
    } catch (error: any) {
      console.error('getSubmissions error:', error);
      console.error('getSubmissions error details:', error.message, error.stack);

      // For permissions errors, throw the error to prevent infinite retries
      if (error.message && error.message.includes('permissions')) {
        throw error;
      }

      // For other errors, return empty array to prevent breaking the UI
      return [];
    }
  },

  async updateSubmissionStatus(submissionId: string, status: string, notes?: string) {
    try {
      const user = await getCurrentAuthUser();

      await updateDoc(doc(db, 'submissions', submissionId), {
        status,
        notes,
        reviewedAt: serverTimestamp(),
        reviewedBy: user.uid,
      });
    } catch (error: any) {
      console.error('updateSubmissionStatus error:', error);
      throw error;
    }
  },

  async getForms() {
    return firebaseForms.getForms();
  },

  async deleteSubmission(submissionId: string) {
    await getCurrentAuthUser();
    await deleteDoc(doc(db, 'submissions', submissionId));
  },

  async deleteSubmissions(submissionIds: string[]) {
    await getCurrentAuthUser();
    if (submissionIds.length === 1) {
      await this.deleteSubmission(submissionIds[0]);
      return;
    }
    const batch = writeBatch(db);
    for (const id of submissionIds) {
      batch.delete(doc(db, 'submissions', id));
    }
    await batch.commit();
  },
};

// Google Drive integration methods
const firebaseGoogleDrive = {
  async updateGoogleDriveConnection(connectionData: {
    connected: boolean;
    userEmail: string;
    connectedAt: string | null;
  }) {
    try {
      const user = await getCurrentAuthUser();
      const org = await this.getCurrentUser();

      if (!org?.organization?.id) {
        throw new Error('Organization not found');
      }

      await setDoc(
        doc(db, 'organizations', org.organization.id, 'integrations', 'googledrive'),
        {
          ...connectionData,
          updatedAt: serverTimestamp(),
          updatedBy: user.uid,
        },
        { merge: true }
      );
    } catch (error: any) {
      console.error('updateGoogleDriveConnection error:', error);
      throw error;
    }
  },

  async updateGoogleDriveSettings(settings: {
    autoBackupEnabled: boolean;
    backupPDFs: boolean;
    backupCSVs: boolean;
    folderName: string;
  }) {
    try {
      const user = await getCurrentAuthUser();
      const org = await this.getCurrentUser();

      if (!org?.organization?.id) {
        throw new Error('Organization not found');
      }

      await setDoc(
        doc(db, 'organizations', org.organization.id, 'settings', 'googledrive'),
        {
          ...settings,
          updatedAt: serverTimestamp(),
          updatedBy: user.uid,
        },
        { merge: true }
      );
    } catch (error: any) {
      console.error('updateGoogleDriveSettings error:', error);
      throw error;
    }
  },

  async getGoogleDriveConnection() {
    try {
      const org = await this.getCurrentUser();

      if (!org?.organization?.id) {
        return null;
      }

      const connectionDoc = await getDoc(
        doc(db, 'organizations', org.organization.id, 'integrations', 'googledrive')
      );

      return connectionDoc.exists() ? connectionDoc.data() : null;
    } catch (error: any) {
      console.error('getGoogleDriveConnection error:', error);
      return null;
    }
  },

  async getGoogleDriveSettings() {
    try {
      const org = await this.getCurrentUser();

      if (!org?.organization?.id) {
        return null;
      }

      const settingsDoc = await getDoc(
        doc(db, 'organizations', org.organization.id, 'settings', 'googledrive')
      );

      return settingsDoc.exists() ? settingsDoc.data() : null;
    } catch (error: any) {
      console.error('getGoogleDriveSettings error:', error);
      return null;
    }
  },
};

// Webhook integration methods
const firebaseWebhooks = {
  async getWebhookEndpoints(): Promise<import('@/types').WebhookEndpoint[]> {
    try {
      const org = await this.getCurrentUser();
      if (!org?.organization?.id) return [];

      const webhookDoc = await getDoc(
        doc(db, 'organizations', org.organization.id, 'integrations', 'webhooks')
      );

      if (!webhookDoc.exists()) return [];
      const data = webhookDoc.data();
      return (data?.endpoints || []) as import('@/types').WebhookEndpoint[];
    } catch (error) {
      console.error('getWebhookEndpoints error:', error);
      return [];
    }
  },

  async saveWebhookEndpoints(endpoints: import('@/types').WebhookEndpoint[]): Promise<void> {
    try {
      const user = await getCurrentAuthUser();
      const org = await this.getCurrentUser();
      if (!org?.organization?.id) throw new Error('Organization not found');

      await setDoc(
        doc(db, 'organizations', org.organization.id, 'integrations', 'webhooks'),
        {
          endpoints,
          updatedAt: serverTimestamp(),
          updatedBy: user.uid,
        },
        { merge: true }
      );
    } catch (error) {
      console.error('saveWebhookEndpoints error:', error);
      throw error;
    }
  },

  async getWebhookLogs(options?: {
    endpointId?: string;
    limit?: number;
  }): Promise<import('@/types').WebhookDeliveryLog[]> {
    try {
      const org = await this.getCurrentUser();
      if (!org?.organization?.id) return [];

      const logsRef = collection(db, 'organizations', org.organization.id, 'webhookLogs');
      let q;
      if (options?.endpointId) {
        q = query(
          logsRef,
          where('endpointId', '==', options.endpointId),
          orderBy('createdAt', 'desc')
        );
      } else {
        q = query(logsRef, orderBy('createdAt', 'desc'));
      }

      const snapshot = await getDocs(q);
      const logs: import('@/types').WebhookDeliveryLog[] = [];
      let count = 0;
      const maxLogs = options?.limit || 50;

      snapshot.forEach((docSnap) => {
        if (count >= maxLogs) return;
        const data = docSnap.data();
        logs.push({
          id: docSnap.id,
          endpointId: data.endpointId,
          endpointName: data.endpointName,
          endpointUrl: data.endpointUrl,
          submissionId: data.submissionId,
          formId: data.formId,
          formTitle: data.formTitle,
          event: data.event,
          status: data.status,
          statusCode: data.statusCode,
          responseBody: data.responseBody,
          error: data.error,
          attempts: data.attempts,
          createdAt: safeToDate(data.createdAt, new Date()) as Date,
          completedAt: safeToDate(data.completedAt) as Date | undefined,
          duration: data.duration,
        });
        count++;
      });

      return logs;
    } catch (error) {
      console.error('getWebhookLogs error:', error);
      return [];
    }
  },

  async testWebhookEndpoint(
    endpointId: string
  ): Promise<{ success: boolean; statusCode?: number; error?: string }> {
    try {
      const org = await this.getCurrentUser();
      if (!org?.organization?.id) throw new Error('Organization not found');

      const testFn = httpsCallable(functions, 'testWebhookEndpoint');
      const result = await testFn({ endpointId, organizationId: org.organization.id });
      return result.data as { success: boolean; statusCode?: number; error?: string };
    } catch (error: any) {
      console.error('testWebhookEndpoint error:', error);
      return { success: false, error: error.message || 'Test failed' };
    }
  },
};

// Export combined API client compatible with existing code
export const firebaseApiClient = {
  ...firebaseAuth,
  ...firebaseForms,
  ...firebaseSubmissions,
  ...firebaseGoogleDrive,
  ...firebaseWebhooks,
};
